import { en, type Dictionary, type MessageKey } from './en.ts';
import { de } from './de.ts';
import { LOCALES, type Locale } from '../utils/validation.ts';

export type { MessageKey };
export const dictionaries: Record<Locale, Dictionary> = { de, en };
export const LOCALE_LABELS: Record<Locale, string> = { de: 'Deutsch', en: 'English' };

const STORAGE_KEY = 'termine:locale';
let current: Locale = 'de';
const listeners = new Set<(l: Locale) => void>();

export function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
}

/** Saved choice > shop default (German market: the browser language is deliberately ignored). */
export function initLocale(tenantDefault: Locale): Locale {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
  current = isLocale(saved) ? saved : tenantDefault;
  document.documentElement.lang = current;
  return current;
}

export function getLocale(): Locale {
  return current;
}

export function setLocale(locale: Locale): void {
  current = locale;
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* storage blocked */
  }
  listeners.forEach((fn) => fn(locale));
}

export function onLocaleChange(fn: (l: Locale) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function t(key: MessageKey, params?: Record<string, string | number>, locale: Locale = current): string {
  const template = dictionaries[locale][key] ?? en[key];
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) => (name in params ? String(params[name]) : m));
}
